import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const API_BASE = API_BASE_URL;

const PLAN_COLORS = {
  community: '#6b7280',
  pro: '#6366f1',
  enterprise: '#f59e0b',
};

const PLAN_LABELS = {
  community: 'Community',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

function PlanBadge({ plan }) {
  const color = PLAN_COLORS[plan] || '#6b7280';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      background: color + '22',
      color: color,
      border: `1px solid ${color}44`,
      fontWeight: 700,
      fontSize: '12px',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}>
      {PLAN_LABELS[plan] || plan}
    </span>
  );
}

function UsageBar({ label, used, limit }) {
  if (limit === 0) {
    return (
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px', color: 'var(--text-primary)' }}>
          <span>{label}</span>
          <span style={{ color: '#10b981', fontWeight: 600 }}>Unlimited</span>
        </div>
      </div>
    );
  }
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const barColor = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#6366f1';
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px', color: 'var(--text-primary)' }}>
        <span>{label}</span>
        <span style={{ color: barColor, fontWeight: 600 }}>{used} / {limit}</span>
      </div>
      <div style={{ height: '5px', borderRadius: '3px', background: 'var(--bg-secondary, #1e1e2e)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: '3px', transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

export default function LicenseManager({ onClose, token, isAdmin, productMode = 'flow' }) {
  const isFormsProduct = productMode === 'forms';
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyInput, setKeyInput] = useState('');
  const [activating, setActivating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchLicense = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/license`, { headers });
      setLicenseInfo(res.data);
    } catch (e) {
      setError('Failed to load license info');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLicense();
  }, [fetchLicense]);

  const handleActivate = async () => {
    if (!keyInput.trim()) return;
    setActivating(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_BASE}/license`, { key: keyInput.trim() }, { headers });
      setLicenseInfo(res.data);
      setKeyInput('');
      setSuccess(`License activated! Plan: ${PLAN_LABELS[res.data.plan] || res.data.plan}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid license key');
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Remove the current license key and revert to the Community plan?')) return;
    setDeactivating(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.delete(`${API_BASE}/license`, { headers });
      setLicenseInfo(res.data);
      setSuccess('License removed. Reverted to Community plan.');
    } catch (e) {
      setError('Failed to remove license');
    } finally {
      setDeactivating(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  };
  const modalStyle = {
    background: 'var(--bg-primary, #0d0d1a)',
    border: '1px solid var(--border-color, #2a2a3e)',
    borderRadius: '12px',
    width: '560px',
    maxWidth: '95vw',
    maxHeight: '88vh',
    overflowY: 'auto',
    padding: '28px',
    color: 'var(--text-primary, #e2e8f0)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>🔑</span>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>License Management</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>
        ) : licenseInfo ? (
          <>
            {/* Current Plan */}
            <div style={{ background: 'var(--bg-secondary, #1a1a2e)', borderRadius: '10px', padding: '18px', marginBottom: '20px', border: '1px solid var(--border-color, #2a2a3e)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current Plan</div>
                  <PlanBadge plan={licenseInfo.plan} />
                </div>
                {licenseInfo.plan !== 'community' && licenseInfo.key_prefix && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Key</div>
                    <code style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--bg-node, #1e1e35)', padding: '2px 6px', borderRadius: '4px' }}>{licenseInfo.key_prefix}</code>
                  </div>
                )}
              </div>

              {licenseInfo.expires_at && (
                <div style={{ fontSize: '12px', color: licenseInfo.is_expired ? '#ef4444' : '#f59e0b', marginBottom: '12px' }}>
                  {licenseInfo.is_expired ? '⚠ Expired: ' : '⏳ Expires: '}{new Date(licenseInfo.expires_at).toLocaleDateString()}
                </div>
              )}
              {licenseInfo.seats > 0 && (
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  👥 {licenseInfo.seats} seat{licenseInfo.seats !== 1 ? 's' : ''}
                </div>
              )}

              {/* Usage */}
              {licenseInfo.usage && (
                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border-color, #2a2a3e)' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Usage This Month</div>
                  {!isFormsProduct && (
                    <>
                      <UsageBar label="Active Workflows" used={licenseInfo.usage.active_workflows} limit={licenseInfo.workflow_limit} />
                      <UsageBar label="Manual Executions" used={licenseInfo.usage.monthly_executions} limit={licenseInfo.execution_limit} />
                    </>
                  )}
                  <UsageBar label="Active Forms" used={licenseInfo.usage.active_forms ?? 0} limit={licenseInfo.form_limit ?? 0} />
                  <UsageBar label="Monthly Submissions" used={licenseInfo.usage.monthly_submissions ?? 0} limit={licenseInfo.submission_limit ?? 0} />
                  <UsageBar label="Workspaces Owned" used={licenseInfo.usage.owned_workspaces} limit={licenseInfo.workspace_limit} />
                </div>
              )}

              {/* Product mismatch warning */}
              {licenseInfo.product && licenseInfo.product === 'forms' && !isFormsProduct && licenseInfo.plan !== 'community' && (
                <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', fontSize: '12px', color: '#f59e0b', lineHeight: 1.5 }}>
                  Your license is for xavvy Forms. Workflow features are limited to community tier. Upgrade to a Flow license for full access.
                </div>
              )}
            </div>

            {/* Feature Table */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Features</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {licenseInfo.features && Object.entries(licenseInfo.features).map(([key, info]) => (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '7px 10px', borderRadius: '7px',
                    background: info.allowed ? 'rgba(16,185,129,0.07)' : 'rgba(107,114,128,0.06)',
                    border: `1px solid ${info.allowed ? 'rgba(16,185,129,0.2)' : 'rgba(107,114,128,0.15)'}`,
                  }}>
                    <span style={{ fontSize: '13px' }}>{info.allowed ? '✓' : '🔒'}</span>
                    <span style={{ fontSize: '12px', color: info.allowed ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: info.allowed ? 500 : 400 }}>
                      {info.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Activate / Change Key (admin only) */}
            {isAdmin && (
              <div style={{ background: 'var(--bg-secondary, #1a1a2e)', borderRadius: '10px', padding: '16px', border: '1px solid var(--border-color, #2a2a3e)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>
                  {licenseInfo.plan === 'community' ? 'Activate a License Key' : 'Update License Key'}
                </div>
                {licenseInfo.plan === 'community' && (
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px 0', lineHeight: 1.6 }}>
                    Purchase a Pro or Enterprise license to unlock AI Copilot, Workflow Metrics, Folders, SSO, Git Sync, and more.
                    Contact <strong>license@xavvy.dev</strong> or visit <strong>xavvy.dev/pricing</strong>.
                  </p>
                )}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    placeholder="xv2.eyJwbGFuIjoicHJvIn0..."
                    style={{
                      flex: 1, padding: '8px 12px', borderRadius: '7px', fontSize: '12px',
                      background: 'var(--bg-node, #1e1e35)', border: '1px solid var(--border-color, #2a2a3e)',
                      color: 'var(--text-primary)', outline: 'none', fontFamily: 'monospace',
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleActivate()}
                  />
                  <button
                    onClick={handleActivate}
                    disabled={activating || !keyInput.trim()}
                    style={{
                      padding: '8px 16px', borderRadius: '7px', fontSize: '12px', fontWeight: 600,
                      background: '#6366f1', color: '#fff', border: 'none', cursor: 'pointer',
                      opacity: activating || !keyInput.trim() ? 0.5 : 1,
                    }}
                  >
                    {activating ? 'Activating...' : 'Activate'}
                  </button>
                </div>

                {licenseInfo.plan !== 'community' && (
                  <button
                    onClick={handleDeactivate}
                    disabled={deactivating}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                      background: 'transparent', color: '#ef4444', border: '1px solid #ef444440',
                      cursor: 'pointer', opacity: deactivating ? 0.5 : 1,
                    }}
                  >
                    {deactivating ? 'Removing...' : 'Remove license key'}
                  </button>
                )}
              </div>
            )}

            {error && (
              <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '7px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: '13px' }}>
                {error}
              </div>
            )}
            {success && (
              <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '7px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', fontSize: '13px' }}>
                {success}
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '30px' }}>Could not load license information.</div>
        )}
      </div>
    </div>
  );
}
